import { Stack } from "@fluentui/react";

import { Invoice } from "../components/Invoice";
import { IInvoiceInfos } from "../components/Invoice/interfaces";
import { PaymentArea } from "../components/PaymentRequest";
import type { ApprovalFlow, Contract } from "../contract.interface";
import { ContractShipments } from "../ContractShipments";

import { ClientInfo } from "./ClientInfo";
import { ContractComplementaryDocuments } from "./ContractComplementaryDocuments";
import { ContractDocuments } from "./ContractDocuments";
import { ContractInfo } from "./ContractInfo";
import { ContractSignatures } from "./ContractSignatures";
import { PaymentCardWrapper } from "./styles";

import { PaymentRequestTypeProviders } from "@/common/types/PaymentRequestTypes";
import {
  PermissionsAction,
  PermissionsPosModule,
  PermissionsScope
} from "@/common/types/permissions.types";
import { DisplayProducts } from "@/components/Contracts/ContractProduct";
import { useGetProfile } from "@/core/libs/api/react-query";
import { ContractStatus, SignatureType } from "@/modules/Contracts/enums";
import { shouldHaveOwnershipTransfer } from "@/modules/Contracts/pages/Edit/rules/shouldHaveOwnershipTransfer";
import { ServicesCategoriesTypesEnum } from "@/modules/Settings/types/ServicesCategories.types";
import { IStep } from "@/modules/Terms/interfaces/ITerm.interface";
import { ActivationFeePaymentInfo } from "@/modules/User/types";

interface ContractInfoProps {
  contract: Contract;
  approvalFlow?: ApprovalFlow;
  clientInfoTitle: string;
  contractInfoTitle: string;
  invoiceInfos: IInvoiceInfos;
  nextStep?: IStep;
  setIsVisible?: (condition: boolean) => void;
  className?: string;
  isInsideStep?: boolean;
  openTotalFeeOverride?: () => void;
  isEditingEnabled?: boolean;
  refetchContractData?: () => Promise<void>;
  defaultProductCompactMode?: boolean;
  toggleActionModal?: () => void;
  setAction?: (action: PermissionsAction) => void;
  setActionMessage?: (actionMessage: string) => void;
  openDrawer?: () => void;
  isInsidePanel?: boolean;
  activationFeePaymentInfo?: ActivationFeePaymentInfo[];
  onOpenRenewalProductsModal?: () => void;
  onOpenOwnershipTransferTermDraftDrawer?: () => void;
  hideContractValues?: boolean;
  hidePaymentArea?: boolean;
}

export function DisplayContractInfo({
  contract,
  approvalFlow,
  clientInfoTitle,
  contractInfoTitle,
  nextStep,
  setIsVisible,
  className,
  isInsideStep,
  isEditingEnabled,
  refetchContractData,
  defaultProductCompactMode,
  openDrawer,
  isInsidePanel,
  onOpenRenewalProductsModal,
  onOpenOwnershipTransferTermDraftDrawer,
  invoiceInfos,
  hidePaymentArea
}: ContractInfoProps): JSX.Element {
  const userProfile = useGetProfile();
  const permissions = userProfile.data?.data?.role.permissions;
  let canSendDocuments = false;
  const approvalFlowSteps = approvalFlow?.steps ?? [];

  const showShipmentsCard =
    Boolean(contract.currentStep) &&
    contract.currentStep.internalName !== ContractStatus.IN_PROGRESS;

  const canManageShipments =
    Boolean(contract.nextStep) &&
    contract.nextStep.internalName !== ContractStatus.PROCESSED;

  const shipmentPermissions =
    permissions?.[PermissionsScope.POS]?.[
      PermissionsPosModule.CONTRACT_SHIPMENTS
    ];
  const canAccessShipments = (action: PermissionsAction) =>
    shipmentPermissions?.some(
      permission =>
        permission === PermissionsAction.ALL || permission === action
    ) ?? false;

  const lastStepToSendDocument = approvalFlowSteps.find(
    step => step.internalName === ContractStatus.SERVICE_DELIVERED
  );

  if (
    lastStepToSendDocument &&
    contract.currentStep?.order >= 10 &&
    contract.currentStep?.order <= lastStepToSendDocument?.order
  ) {
    canSendDocuments = true;
  }

  const isTagService =
    contract?.serviceCategory?.type === ServicesCategoriesTypesEnum.TAG;

  const isInvoicePayment =
    contract?.paymentRequest?.[0]?.typeProvider ===
    PaymentRequestTypeProviders.INVOICE_DEBIT;

  const hasOfferPricePaymentMethod = contract?.items?.some(
    item => item?.offerPrice?.offerPricePaymentMethod?.length > 0
  );

  const displayPaymentArea =
    !hidePaymentArea &&
    approvalFlowSteps.some(step => step.internalName === "SETUP_PAID") &&
    contract.paymentRequest?.length > 0 &&
    Number(contract.activationInstallment?.total) > 0 &&
    !isInvoicePayment;

  const contractOwnershipTransferPermissionModule =
    permissions?.[PermissionsScope.POS]?.[
      PermissionsPosModule.CONTRACTS_CHANGE_OF_OWNERSHIP_RECTIFICATION
    ];

  const canCreateOwnershipTransferTermDraft =
    shouldHaveOwnershipTransfer(contract) &&
    contractOwnershipTransferPermissionModule?.some(
      contractAction => contractAction === PermissionsAction.CREATE
    );

  return (
    <Stack>
      <div style={{ marginTop: 20 }}>
        <Stack tokens={{ childrenGap: 15 }}>
          <Stack horizontal wrap tokens={{ childrenGap: 15 }}>
            <Stack.Item className="ms-sm12 ms-xl5" grow>
              <ClientInfo
                title={clientInfoTitle}
                contract={contract}
                openDrawer={openDrawer}
                isInsidePanel={isInsidePanel}
              />
            </Stack.Item>

            <Stack.Item className="ms-sm12 ms-xl5" grow>
              <ContractInfo
                title={contractInfoTitle}
                contract={contract}
                isInsidePanel={isInsidePanel}
              />
            </Stack.Item>
          </Stack>

          {showShipmentsCard && canAccessShipments(PermissionsAction.LIST) && (
            <ContractShipments
              contractId={contract.id}
              canListCosts={canAccessShipments(PermissionsAction.LIST_COSTS)}
              canCreate={
                canManageShipments &&
                canAccessShipments(PermissionsAction.CREATE)
              }
              canUpdate={
                canManageShipments &&
                canAccessShipments(PermissionsAction.UPDATE)
              }
            />
          )}

          <DisplayProducts
            items={contract.items}
            contract={contract}
            isEditingEnabled={isEditingEnabled}
            nextStep={nextStep}
            currentStep={contract.currentStep}
            refetchContractData={refetchContractData}
            defaultCompactMode={defaultProductCompactMode}
            serviceCategoryType={contract.serviceCategory.type}
            businessUnitId={contract.businessUnit.id}
            service={contract.service.id}
            onOpenRenewalProductsModal={onOpenRenewalProductsModal}
            businessUnit={contract.businessUnit}
            approvalFlow={approvalFlow}
          />

          <PaymentCardWrapper isInsidePanel={isInsidePanel}>
            {(!isTagService || hasOfferPricePaymentMethod) && (
              <Invoice
                invoiceInfos={invoiceInfos}
                paymentMethodName={contract?.setupPaymentMethod?.name}
                paymentMethodType={contract?.setupPaymentMethod?.type}
                isInsidePanel={isInsidePanel}
                isTagService={isTagService}
              />
            )}

            {displayPaymentArea && (
              <PaymentArea
                paymentType={contract?.setupPaymentMethod?.type}
                paymentRequest={contract?.paymentRequest?.[0]}
                canShowConfirmPaymentButton={
                  contract?.nextStep?.internalName === "SETUP_PAID"
                }
                contractId={contract.id}
                refetchContractData={refetchContractData}
              />
            )}
          </PaymentCardWrapper>
          <Stack horizontal wrap tokens={{ childrenGap: 15 }}>
            {/* VERIFY IF IS INSIDE A STEP, IF IS INSIDE A STEP, DON'T HAVE SIGNATURES AND DOCUMENTS */}
            {!isInsideStep && (
              <>
                {[
                  SignatureType.ELETRONIC,
                  SignatureType.ELETRONIC_WHATSAPP
                ].includes(contract?.signature?.type) && (
                  <Stack.Item className={className} grow>
                    <ContractSignatures
                      contractId={contract.id}
                      eletronicSignature={contract.eletronicSignature}
                      account={contract.account}
                      businessUnitId={contract.businessUnitId}
                      refetchContractData={refetchContractData}
                    />
                  </Stack.Item>
                )}
                {contract?.terms.length > 0 && (
                  <Stack.Item className={className} grow>
                    <ContractComplementaryDocuments
                      terms={contract.terms}
                      canCreate={canCreateOwnershipTransferTermDraft}
                      onCreate={onOpenOwnershipTransferTermDraftDrawer}
                    />
                  </Stack.Item>
                )}
                <Stack.Item className={className} grow>
                  <ContractDocuments
                    contractId={contract.id}
                    canSendDocument={canSendDocuments}
                    isSendingDocumentStep={
                      nextStep?.internalName === ContractStatus.DOCUMENTS_SENT
                    }
                    setIsVisible={setIsVisible ? setIsVisible : null}
                  />
                </Stack.Item>
              </>
            )}
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
}
